import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button } from '@mui/material';
import { Iconify } from 'src/components';
import {
  scriptCardSx,
  scrollableScriptCardSx,
  scrollableCardStepIndicatorSx,
  skippedBannerSx,
  cardHeaderSx,
  cardBadgeSx,
  cardTitleSx,
  cardSubtitleSx,
  cardBodySx,
} from '../styles';

const ScriptCard = forwardRef(({
  badge,
  badgeClass = 's1',
  title,
  subtitle,
  wasSkipped = false,
  hasRequiredFields = false,
  onUnskip,
  headerRight,
  children,
  // Scrollable view props
  isScrollableView = false,
  isActive = false,
  isCollapsed = false,
  isCompleted = false,
  stepNumber,
  onClick,
}, ref) => {
  // Use red styling when skipped card has required fields
  const showRequiredWarning = wasSkipped && hasRequiredFields;

  const cardSx = isScrollableView
    ? scrollableScriptCardSx(isActive, wasSkipped, hasRequiredFields)
    : scriptCardSx(wasSkipped, hasRequiredFields);

  // Collapsed card — compact header-only view for non-active cards in scrollable mode
  if (isCollapsed) {
    return (
      <Box
        ref={ref}
        onClick={onClick}
        sx={{
          ...cardSx,
          cursor: 'pointer',
          py: 1.5,
          px: 2,
          minHeight: 'auto',
          transition: 'all 0.15s ease',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          },
        }}
      >
        {wasSkipped && showRequiredWarning && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 0.5,
            px: 0.5,
          }}>
            <Iconify icon="mdi:alert-circle" width={14} height={14} sx={{ color: '#FF5630' }} />
            <Typography sx={{ fontSize: '0.6875rem', color: '#FF5630', fontWeight: 600 }}>Required field skipped</Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {stepNumber != null && (
            <Box sx={{
              ...scrollableCardStepIndicatorSx(false),
              width: '1.5rem',
              height: '1.5rem',
              minWidth: '1.5rem',
              fontSize: '0.6875rem',
              ...(isCompleted && {
                bgcolor: '#00AB55',
                color: '#fff',
                borderColor: '#00AB55',
              }),
              ...(wasSkipped && showRequiredWarning && {
                bgcolor: '#FF5630',
                color: '#fff',
                borderColor: '#FF5630',
              }),
              ...(wasSkipped && !showRequiredWarning && {
                bgcolor: '#FFAB00',
                color: '#fff',
                borderColor: '#FFAB00',
              }),
            }}>
              {(() => {
                if (isCompleted) return <Iconify icon="mdi:check" width={12} height={12} />;
                if (wasSkipped) return <Iconify icon="mdi:redo" width={12} height={12} />;
                return stepNumber;
              })()}
            </Box>
          )}
          <Typography sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'text.primary',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {title}
          </Typography>
          <Iconify
            icon="mdi:chevron-down"
            width={18}
            height={18}
            sx={{ color: 'text.secondary', flexShrink: 0 }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      sx={cardSx}
      onClick={isScrollableView && !isActive && onClick ? onClick : undefined}
    >
      {wasSkipped && (
        <Box sx={skippedBannerSx(hasRequiredFields)}>
          <Iconify icon={showRequiredWarning ? "mdi:alert-circle" : "mdi:alert"} className="icon" width={18} height={18} />
          <span>{showRequiredWarning ? 'Required field was skipped!' : 'This question was previously skipped'}</span>
          {onUnskip && (
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onUnskip();
              }}
              sx={{
                ml: 'auto',
                padding: '6px 14px',
                bgcolor: showRequiredWarning ? '#FF5630' : '#FFAB00',
                color: '#fff',
                borderRadius: 1,
                fontSize: 12,
                fontWeight: 600,
                '&:hover': { bgcolor: showRequiredWarning ? '#B71D18' : '#B76E00' },
              }}
            >
              Answer Now
            </Button>
          )}
        </Box>
      )}

      <Box sx={cardHeaderSx}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            {isScrollableView && stepNumber != null && (
              <Box sx={scrollableCardStepIndicatorSx(isActive)}>
                {stepNumber}
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              {badge && (
                <Box sx={cardBadgeSx(badgeClass)}>
                  {badge}
                </Box>
              )}
              <Typography sx={cardTitleSx}>{title}</Typography>
              {subtitle && (
                <Typography sx={cardSubtitleSx}>{subtitle}</Typography>
              )}
            </Box>
          </Box>
          {headerRight && (
            <Box sx={{ flexShrink: 0 }}>
              {headerRight}
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={cardBodySx}>
        {children}
      </Box>
    </Box>
  );
});

ScriptCard.displayName = 'ScriptCard';

ScriptCard.propTypes = {
  badge: PropTypes.string,
  badgeClass: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  wasSkipped: PropTypes.bool,
  hasRequiredFields: PropTypes.bool,
  onUnskip: PropTypes.func,
  headerRight: PropTypes.node,
  children: PropTypes.node,
  isScrollableView: PropTypes.bool,
  isActive: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  isCompleted: PropTypes.bool,
  stepNumber: PropTypes.number,
  onClick: PropTypes.func,
};

export default ScriptCard;
